import { FC, ReactNode } from 'react';
import { Avatar, Button, Card, Col, Row, Space, Typography } from 'antd';
import { TbX } from 'react-icons/tb';
import { Popup } from 'react-map-gl/maplibre';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import '../styles.css';

export interface ThemedMapPopupItem {
    label: string;
    value: ReactNode;
}

interface ThemedMapPopupProps {
    icon?: ReactNode;
    items?: ThemedMapPopupItem[];
    latitude: number;
    longitude: number;
    onClose: () => void;
    subtitle?: ReactNode;
    title: ReactNode;
}

const ThemedMapPopup: FC<ThemedMapPopupProps> = ({
    icon,
    items = [],
    latitude,
    longitude,
    onClose,
    subtitle,
    title,
}) => {
    const { token } = useAntDesignTheme();
    const hasBody = subtitle !== undefined || items.length > 0;

    return (
        <Popup
            anchor='top'
            className='cc-themed-map-popup'
            closeButton={false}
            closeOnClick={false}
            latitude={latitude}
            longitude={longitude}
            maxWidth='24rem'
            offset={12}
            onClose={onClose}
        >
            <Card
                size='small'
                styles={{ body: { padding: 0 } }}
                style={{
                    borderColor: token.colorBorderSecondary,
                    borderRadius: token.borderRadius,
                    boxShadow: token.boxShadowSecondary,
                    minWidth: '15rem',
                    overflow: 'hidden',
                }}
            >
                <Row
                    align='middle'
                    style={{
                        background: token.colorBgElevated,
                        borderBottom: hasBody ? `1px solid ${token.colorBorderSecondary}` : undefined,
                        minHeight: '2.5rem',
                        padding: '0.5rem 0.5rem 0.5rem 0.75rem',
                    }}
                    wrap={false}
                >
                    {icon ? (
                        <Col flex='none'>
                            <Avatar
                                icon={icon}
                                shape='square'
                                size={22}
                                style={{
                                    background: token.colorFillSecondary,
                                    border: `1px solid ${token.colorBorderSecondary}`,
                                    color: token.colorTextSecondary,
                                }}
                            />
                        </Col>
                    ) : null}
                    <Col flex='auto' style={{ minWidth: 0, paddingLeft: icon ? '0.5rem' : 0 }}>
                        <Typography.Text ellipsis strong style={{ display: 'block', fontSize: token.fontSizeSM }}>
                            {title}
                        </Typography.Text>
                    </Col>
                    <Col flex='none'>
                        <Button
                            aria-label='Close map details'
                            icon={<TbX />}
                            onClick={onClose}
                            size='small'
                            type='text'
                        />
                    </Col>
                </Row>
                {hasBody ? (
                    <Space direction='vertical' size={6} style={{ padding: '0.625rem 0.75rem 0.75rem', width: '100%' }}>
                        {subtitle !== undefined ? (
                            <Typography.Text style={{ color: token.colorTextTertiary, fontSize: token.fontSizeSM }}>
                                {subtitle}
                            </Typography.Text>
                        ) : null}
                        {items.map((item) => (
                            <Row
                                align='top'
                                justify='space-between'
                                key={item.label}
                                style={{ gap: '1rem' }}
                                wrap={false}
                            >
                                <Col flex='none'>
                                    <Typography.Text
                                        style={{ color: token.colorTextTertiary, fontSize: token.fontSizeSM }}
                                    >
                                        {item.label}
                                    </Typography.Text>
                                </Col>
                                <Col flex='auto' style={{ minWidth: 0, textAlign: 'right' }}>
                                    <Typography.Text
                                        ellipsis
                                        style={{ color: token.colorText, display: 'block', fontSize: token.fontSizeSM }}
                                    >
                                        {item.value}
                                    </Typography.Text>
                                </Col>
                            </Row>
                        ))}
                    </Space>
                ) : null}
            </Card>
        </Popup>
    );
};

export default ThemedMapPopup;
